import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../../../../helpers/api";
import type { LinkedAccountsResponse } from "../../../../types/linked-accounts";
import { LinkAccountDialog } from "./LinkAccountDialog";
import { UnlinkConfirm } from "./UnlinkConfirm";

interface LinkedAccountsSectionProps {
  userId: string;
  userDisplayName?: string;
}

export function LinkedAccountsSection({
  userId,
  userDisplayName,
}: LinkedAccountsSectionProps) {
  const queryClient = useQueryClient();
  const [linkOpen, setLinkOpen] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState<
    null | { userId: string; label: string }
  >(null);

  const { data, isLoading } = useQuery<LinkedAccountsResponse>({
    queryKey: ["linked-accounts", userId],
    queryFn: () => api.getLinkedAccounts(userId) as Promise<LinkedAccountsResponse>,
    enabled: !!userId,
  });

  const linkMutation = useMutation({
    mutationFn: (otherUserId: string) => api.linkAccount(userId, otherUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["linked-accounts", userId] });
      // Also refresh the users list so the badge column updates.
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: (targetUserId: string) => api.unlinkAccount(targetUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["linked-accounts", userId] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });

  const others = (data?.members ?? []).filter((m) => !m.isCurrent);
  const isLinked = (data?.members ?? []).length >= 2;

  return (
    <section
      className="bg-white rounded-xl border border-border-secondary shadow-sm flex flex-col"
      data-testid="linked-accounts-section"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-secondary">
        <h2 className="text-sm font-semibold text-content-primary">
          Linked accounts
        </h2>
        {!isLinked && !isLoading && (
          <button
            type="button"
            onClick={() => setLinkOpen(true)}
            data-testid="link-to-another-account-cta"
            className="text-xs font-medium text-primary hover:text-primary/80"
          >
            Link to another account
          </button>
        )}
        {isLinked && (
          <button
            type="button"
            onClick={() => setLinkOpen(true)}
            data-testid="link-another-account"
            className="text-xs font-medium text-primary hover:text-primary/80"
          >
            Link another
          </button>
        )}
      </div>

      <div className="flex-1 p-4">
        {isLoading && (
          <div className="text-content-tertiary text-xs">Loading…</div>
        )}

        {!isLoading && !isLinked && (
          <p className="text-xs text-content-tertiary italic">
            {userDisplayName ?? "This user"} is not part of any linked account
            group.
          </p>
        )}

        {!isLoading && isLinked && (
          <ul className="divide-y divide-border-secondary">
            {others.map((m) => (
              <li
                key={m.userId}
                data-testid={`linked-row-${m.userId}`}
                className="flex items-center justify-between py-2 first:pt-0 last:pb-0"
              >
                <div className="min-w-0">
                  <div className="text-content-primary text-xs font-medium truncate">
                    {m.firstName} {m.lastName}
                  </div>
                  <div className="text-content-tertiary text-[11px] truncate">
                    {m.email} · {m.hotelName ?? "—"}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setUnlinkTarget({
                      userId: m.userId,
                      label: m.hotelName ?? m.email,
                    })
                  }
                  disabled={unlinkMutation.isPending}
                  data-testid={`unlink-${m.userId}`}
                  className="text-[11px] font-medium px-2 py-1 rounded-md border border-border-primary text-content-primary hover:bg-surface-secondary/50 disabled:opacity-50"
                >
                  Unlink
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {linkOpen && (
        <LinkAccountDialog
          subjectUserId={userId}
          onClose={() => setLinkOpen(false)}
          onConfirm={(otherUserId) => {
            linkMutation.mutate(otherUserId, {
              onSuccess: () => setLinkOpen(false),
            });
          }}
          isSubmitting={linkMutation.isPending}
        />
      )}

      {unlinkTarget && (
        <UnlinkConfirm
          label={unlinkTarget.label}
          onCancel={() => setUnlinkTarget(null)}
          onConfirm={() => {
            const target = unlinkTarget.userId;
            unlinkMutation.mutate(target, {
              onSuccess: () => setUnlinkTarget(null),
            });
          }}
          isSubmitting={unlinkMutation.isPending}
        />
      )}
    </section>
  );
}
